import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { ShieldCheck, ShieldAlert, Loader2, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

const HostPanVerification = ({ hostId, hostName, onVerifiedChange }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ pan: '', name: '', dob: '' });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${BACKEND_URL}/api/host/verification-status/${hostId}`);
        if (!cancelled && res.data.success) setStatus(res.data.data);
      } catch (e) {
        console.warn('No prior PAN verification found for host:', e?.response?.status || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (hostId) load();
    return () => { cancelled = true; };
  }, [hostId]);

  // Notify parent whenever verification status changes so it can un-gate features
  // (e.g., "Add New Property" button) without requiring a page refresh.
  useEffect(() => {
    if (typeof onVerifiedChange === 'function') {
      onVerifiedChange(!!status?.verified);
    }
  }, [status?.verified, onVerifiedChange]);

  // Prefill name from props when dialog opens
  const openDialog = () => {
    if (hostName && !form.name) setForm((f) => ({ ...f, name: hostName }));
    setOpen(true);
  };

  const handleVerify = async (e) => {
    e?.preventDefault?.();
    const pan = form.pan.trim().toUpperCase();
    const name = form.name.trim();
    const dob = form.dob.trim();

    if (!PAN_REGEX.test(pan)) {
      toast.error('PAN must be 10 characters: 5 letters + 4 digits + 1 letter (e.g., ABCDE1234F)');
      return;
    }
    if (!name) {
      toast.error('Please enter the full name as it appears on the PAN card');
      return;
    }
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(dob)) {
      toast.error('Date of birth must be in DD/MM/YYYY format');
      return;
    }

    try {
      setSubmitting(true);
      const res = await axios.post(`${BACKEND_URL}/api/host/verify-pan`, {
        host_id: hostId,
        pan,
        name,
        date_of_birth: dob,
      });
      if (res.data.success && res.data.verified) {
        toast.success('PAN verified successfully!');
        setOpen(false);
        setForm({ pan: '', name: '', dob: '' });
      } else {
        const remarks = res.data.data?.remarks || 'PAN verification failed';
        toast.error(`Verification failed — ${remarks}`);
      }
      setStatus(res.data.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'PAN verification failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDobChange = (val) => {
    // Auto-format as DD/MM/YYYY: keep digits and slashes only
    const digits = val.replace(/\D/g, '').slice(0, 8);
    let out = digits;
    if (digits.length > 4) out = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    else if (digits.length > 2) out = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    setForm((f) => ({ ...f, dob: out }));
  };

  if (loading) {
    return (
      <Card data-testid="host-pan-verification-card">
        <CardContent className="py-6 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-[#7CB342]" />
        </CardContent>
      </Card>
    );
  }

  const verified = !!status?.verified;

  return (
    <>
      {verified ? (
        // Compact verified bar
        <div
          className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
          data-testid="host-pan-verification-card"
        >
          <div className="flex items-center gap-3" data-testid="pan-verified-banner">
            <div className="h-9 w-9 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="h-5 w-5 text-white" />
            </div>
            <div className="leading-tight">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-green-900">PAN Verified</span>
                <Badge className="bg-green-600 text-white hover:bg-green-600 text-[10px] px-1.5 py-0">
                  <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" /> KYC
                </Badge>
              </div>
              <p className="text-xs text-green-800 mt-0.5">
                <span className="font-mono font-semibold">{status.pan_masked}</span>
                {status.category ? <> · {status.category}</> : null}
                {status.verified_at && (
                  <> · Verified on {new Date(status.verified_at).toLocaleDateString()}</>
                )}
              </p>
            </div>
          </div>
          <span className="text-xs text-green-700/70 sm:text-right">Trusted host · marketplace listing enabled</span>
        </div>
      ) : (
        // Prominent unverified widget — full-width banner with progress steps
        <Card
          className="border-2 border-orange-300 bg-gradient-to-br from-orange-50 via-amber-50 to-white shadow-md overflow-hidden"
          data-testid="host-pan-verification-card"
        >
          <CardContent className="p-0">
            <div className="flex flex-col md:flex-row">
              {/* Left side: icon + status */}
              <div className="bg-gradient-to-br from-orange-500 to-amber-600 text-white p-5 md:w-64 flex-shrink-0 flex flex-col justify-center">
                <div className="h-12 w-12 rounded-full bg-white/20 flex items-center justify-center mb-3 backdrop-blur-sm">
                  <ShieldAlert className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-bold leading-tight">Action Required</h3>
                <p className="text-sm text-orange-50 mt-1">Verify your PAN to start hosting</p>
              </div>

              {/* Right side: content */}
              <div className="flex-1 p-5">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-gray-900">PAN Verification (KYC)</h4>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">
                    Step 1 of 2
                  </span>
                </div>

                <p className="text-sm text-gray-700 mb-3">
                  For trust and compliance, every host must verify their PAN against Income Tax Department
                  records (via Sandbox.co.in). Your PAN is encrypted at rest.
                </p>

                {/* Progress bar */}
                <div className="mb-3">
                  <div className="flex items-center gap-3 text-xs text-gray-600 mb-1.5">
                    <span className="flex items-center gap-1">
                      <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                      Account created
                    </span>
                    <span className="flex items-center gap-1 font-semibold text-orange-700">
                      <ShieldAlert className="h-3.5 w-3.5" />
                      PAN verification
                    </span>
                    <span className="flex items-center gap-1 text-gray-400">
                      Start hosting
                    </span>
                  </div>
                  <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full w-1/2 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full"></div>
                  </div>
                </div>

                {status?.remarks && (
                  <p className="text-xs text-orange-700 bg-orange-100/60 px-3 py-1.5 rounded-md mb-3">
                    Last attempt: {status.remarks}
                  </p>
                )}

                <Button
                  onClick={openDialog}
                  size="lg"
                  className="bg-gradient-to-r from-[#7CB342] to-[#6da036] hover:from-[#6da036] hover:to-[#5c8a2c] text-white shadow-md w-full md:w-auto"
                  data-testid="verify-pan-trigger"
                >
                  <ShieldCheck className="h-4 w-4 mr-2" />
                  Verify PAN now — it takes less than a minute
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-[#7CB342]" />
              Verify your PAN
            </DialogTitle>
            <DialogDescription>
              We use Sandbox.co.in (a NeGD-authorized partner) to verify against the Income Tax
              department records. Your PAN is encrypted at rest.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleVerify} className="space-y-4 py-2">
            <div>
              <Label htmlFor="pan-name">Full Name (as on PAN) *</Label>
              <Input
                id="pan-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="As printed on the PAN card"
                data-testid="pan-name-input"
                autoComplete="name"
              />
            </div>
            <div>
              <Label htmlFor="pan-number">PAN Number *</Label>
              <Input
                id="pan-number"
                value={form.pan}
                onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10) })}
                placeholder="ABCDE1234F"
                maxLength={10}
                className="uppercase font-mono"
                data-testid="pan-number-input"
              />
              <p className="text-xs text-gray-500 mt-1">10 chars: 5 letters + 4 digits + 1 letter</p>
            </div>
            <div>
              <Label htmlFor="pan-dob">Date of Birth (DD/MM/YYYY) *</Label>
              <Input
                id="pan-dob"
                value={form.dob}
                onChange={(e) => handleDobChange(e.target.value)}
                placeholder="01/01/1990"
                inputMode="numeric"
                maxLength={10}
                data-testid="pan-dob-input"
              />
            </div>
            <p className="text-xs text-gray-500">
              By submitting, you consent to PAN verification against Income Tax records via
              Sandbox.co.in.
            </p>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#7CB342] hover:bg-[#6da036] text-white"
                disabled={submitting}
                data-testid="pan-verify-submit"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <ShieldCheck className="h-4 w-4 mr-2" />
                )}
                Verify PAN
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HostPanVerification;
